import { FC } from "react";
import debounce from "lodash.debounce";
import { useForm } from "react-hook-form";
import TextareaAutosize from "react-textarea-autosize";
import * as z from "zod";

import { toast } from "@/components/_ui/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";

import { useUpdateClimateAction } from "../hooks/api/use-update-climate-action";

const formSchema = z.object({
  title: z.string().min(1, "Der Titel darf nicht leer sein"), // Ensure title is not empty
});

export interface ClimateActionTitleEditFormProps {
  className?: string;
  isEditing: boolean;
  climateActionFormData: {
    id: string;
    title: string;
  };
}

export const ClimateActionTitleEditForm: FC<ClimateActionTitleEditFormProps> = ({
  climateActionFormData,
  isEditing,
}) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: climateActionFormData.title,
    },
  });

  const { updateClimateAction } = useUpdateClimateAction({
    onError: () => {
      toast({
        title: "Fehler",
        description:
          "Leider ist ein Fehler aufgetreten. Versuchen Sie die Seite neu zu laden und es erneut zu probieren.",
        variant: "error",
      });
    },
  });

  // Debounced save function
  const debouncedSaveChanges = debounce(async () => {
    const title = form.getValues("title");
    if (title.trim() === "") {
      return;
    }
    await updateClimateAction({
      variables: {
        input: {
          id: climateActionFormData.id,
          title,
        },
      },
    });
  }, 500);

  // Handle form changes
  const handleFormChange = () => {
    debouncedSaveChanges();
  };

  return (
    <form className="mx-auto flex w-full flex-row items-start gap-2" onChange={handleFormChange}>
      <TextareaAutosize
        disabled={!isEditing}
        autoFocus
        id="title"
        placeholder="Name der Maßnahme"
        className="font-base w-full resize-none appearance-none overflow-hidden bg-transparent p-3 pl-0 text-xl focus:outline-none"
        {...form.register("title")}
      />
    </form>
  );
};
